// src/constants/indexInstruments.ts
// Extensible Global Index and Index CFD Registry
// Mirrors backend registry in server/src/constants/indexInstruments.js

export type IndexInstrumentType = 'INDEX_CFD' | 'CASH_INDEX';

export type GlobalIndexDefinition = {
  canonicalSymbol: string;
  indexName: string;
  aliases: readonly string[];
  cashIndexSymbols: readonly string[];
};

export type ResolvedIndexInstrument = {
  symbol: string;
  canonicalSymbol: string;
  indexName: string;
  assetClass: 'STOCKS';
  instrumentType: IndexInstrumentType;
  isRecognizedInstrument: true;
  isSupportedAssetClass: true;
  chartSupported: boolean;
  chartSymbol: string | null;
};

export const GLOBAL_INDEX_DEFINITIONS: readonly GlobalIndexDefinition[] = [
  {
    canonicalSymbol: 'US30',
    indexName: 'Dow Jones Industrial Average',
    aliases: [
      'US30', 'DJ30', 'WS30', 'DJI', 'DJI30',
      'US30CASH', 'DJ30CASH', 'WS30CASH', 'WALLSTREET30', 'US30INDEX', 'US30CFD',
    ],
    cashIndexSymbols: ['^DJI', 'DJI'],
  },
  {
    canonicalSymbol: 'US100',
    indexName: 'Nasdaq 100',
    aliases: [
      'US100', 'NAS100', 'USTEC', 'NDX', 'NDX100',
      'US100CASH', 'NAS100CASH', 'USTECCASH', 'UT100', 'NASDAQ100', 'US100INDEX', 'US100CFD',
    ],
    cashIndexSymbols: ['^NDX', 'NDX'],
  },
  {
    canonicalSymbol: 'US500',
    indexName: 'S&P 500',
    aliases: [
      'US500', 'SPX500', 'SP500', 'SPX', 'GSPC',
      'US500CASH', 'SPX500CASH', 'SP500CASH', 'US500INDEX', 'US500CFD',
    ],
    cashIndexSymbols: ['^GSPC', '^SPX', 'GSPC', 'SPX'],
  },
  {
    canonicalSymbol: 'GER40',
    indexName: 'Germany DAX',
    aliases: [
      'GER40', 'DE40', 'DAX40', 'GER30', 'DE30', 'DAX30', 'GDAXI',
      'DE40CASH', 'GER40CASH', 'DAX40CASH', 'GERMANY40', 'GER40INDEX', 'DE40INDEX',
    ],
    cashIndexSymbols: ['^GDAXI', 'GDAXI'],
  },
  {
    canonicalSymbol: 'UK100',
    indexName: 'UK FTSE 100',
    aliases: [
      'UK100', 'FTSE100', 'UKX',
      'UK100CASH', 'FTSE100CASH', 'GBR100', 'UK100INDEX', 'FTSE100INDEX',
    ],
    cashIndexSymbols: ['^FTSE', 'UKX'],
  },
  {
    canonicalSymbol: 'JP225',
    indexName: 'Japan Nikkei 225',
    aliases: [
      'JP225', 'JPN225', 'NIK225', 'N225', 'NI225',
      'JP225CASH', 'JPN225CASH', 'NIKKEI225', 'JP225INDEX',
    ],
    cashIndexSymbols: ['^N225', 'N225', 'NI225'],
  },
  {
    canonicalSymbol: 'FRA40',
    indexName: 'France CAC 40',
    aliases: [
      'FRA40', 'FR40', 'CAC40', 'F40', 'FCHI',
      'FRA40CASH', 'FR40CASH', 'CAC40CASH', 'FRA40INDEX',
    ],
    cashIndexSymbols: ['^FCHI', 'FCHI'],
  },
  {
    canonicalSymbol: 'AUS200',
    indexName: 'Australia ASX 200',
    aliases: [
      'AUS200', 'AU200', 'ASX200', 'AXJO',
      'AUS200CASH', 'AU200CASH', 'ASX200CASH', 'AUS200INDEX',
    ],
    cashIndexSymbols: ['^AXJO', 'AXJO'],
  },
  {
    canonicalSymbol: 'HK50',
    indexName: 'Hong Kong Hang Seng',
    aliases: [
      'HK50', 'HKG50', 'HSI50', 'HSI',
      'HK50CASH', 'HKG50CASH', 'HANGSENG50', 'HK50INDEX',
    ],
    cashIndexSymbols: ['^HSI', 'HSI'],
  },
  {
    canonicalSymbol: 'US2000',
    indexName: 'US Russell 2000',
    aliases: ['US2000', 'USRUS', 'RUS2000', 'US2000CASH', 'RUS2000CASH'],
    cashIndexSymbols: ['^RUT'],
  },
  {
    canonicalSymbol: 'EU50',
    indexName: 'Euro Stoxx 50',
    aliases: ['EU50', 'EUSTX50', 'STOXX50', 'SX5E', 'EU50CASH', 'STOXX50CASH'],
    cashIndexSymbols: ['^STOXX50E', 'SX5E'],
  },
  {
    canonicalSymbol: 'ESP35',
    indexName: 'Spain IBEX 35',
    aliases: ['ESP35', 'IBEX35', 'ESP35CASH', 'IBEX35CASH'],
    cashIndexSymbols: ['^IBEX'],
  },
  {
    canonicalSymbol: 'ITA40',
    indexName: 'Italy FTSE MIB 40',
    aliases: ['ITA40', 'ITA40CASH'],
    cashIndexSymbols: [],
  },
  {
    canonicalSymbol: 'SWI20',
    indexName: 'Switzerland SMI 20',
    aliases: ['SWI20', 'SMI20', 'SWI20CASH', 'SMI20CASH'],
    cashIndexSymbols: ['^SSMI'],
  },
  {
    canonicalSymbol: 'NETH25',
    indexName: 'Netherlands AEX 25',
    aliases: ['NETH25', 'AEX25', 'NETH25CASH', 'AEX25CASH'],
    cashIndexSymbols: ['^AEX'],
  },
  {
    canonicalSymbol: 'CHINA50',
    indexName: 'China A50',
    aliases: ['CHINA50', 'CN50', 'CHN50', 'CHINA50CASH', 'CN50CASH'],
    cashIndexSymbols: [],
  },
  {
    canonicalSymbol: 'SG30',
    indexName: 'Singapore 30',
    aliases: ['SG30', 'SG30CASH'],
    cashIndexSymbols: [],
  },
  {
    canonicalSymbol: 'IND50',
    indexName: 'India 50',
    aliases: ['IND50', 'NIFTY50', 'IND50CASH'],
    cashIndexSymbols: ['^NSEI'],
  },
] as const;

const HINT_ONLY_INDEX_ALIASES = new Map<string, { canonicalSymbol: string; indexName: string }>([
  ['DAX', { canonicalSymbol: 'GER40', indexName: 'Germany DAX' }],
  ['FTSE', { canonicalSymbol: 'UK100', indexName: 'UK FTSE 100' }],
  ['CAC', { canonicalSymbol: 'FRA40', indexName: 'France CAC 40' }],
  ['ASX', { canonicalSymbol: 'AUS200', indexName: 'Australia ASX 200' }],
]);

const ALIAS_TO_DEFINITION = new Map<string, GlobalIndexDefinition>();

for (const def of GLOBAL_INDEX_DEFINITIONS) {
  for (const alias of def.aliases) {
    ALIAS_TO_DEFINITION.set(alias.toUpperCase(), def);
  }
  for (const cashSym of def.cashIndexSymbols) {
    ALIAS_TO_DEFINITION.set(cashSym.replace(/^\^/, '').toUpperCase(), def);
  }
}

export function normalizeIndexCfdCandidate(symbol: unknown): string {
  const raw = String(symbol ?? '').trim().toUpperCase();
  if (!raw) return '';
  const noVenue = raw.includes(':') ? raw.split(':').pop() || raw : raw;
  const withoutCaret = noVenue.replace(/^\^+/, '');
  const withoutBrokerSuffix = withoutCaret
    .replace(/\.(?:CASH|CFD|IND|INDEX|PRO|RAW|ECN|STD|MINI|MICRO|M|I|C)$/i, '')
    .replace(/(?:[._/-](?:CASH|CFD|IND|INDEX|PRO|RAW|ECN|STD|MINI|MICRO|USD|EUR|GBP|JPY|AUD|CHF|CAD|HKD))$/i, '');
  return withoutBrokerSuffix.replace(/[\s._/-]/g, '');
}

export function hasIndexCfdHint(hint?: unknown, row?: Record<string, any> | null): boolean {
  const explicitHint = String(hint ?? '').trim();
  if (/^(?:cfd|index|indices|ind|index[_\s-]*cfd|cfd[_\s-]*index|cash[_\s-]*index)$/i.test(explicitHint)) {
    return true;
  }
  const candidates = [
    hint,
    row?.instrumentType,
    row?.instrument_type,
    row?.instrument,
    row?.market,
    row?.marketType,
    row?.securityType,
    row?.secType,
    row?.volume,
    row?.Volumen,
    row?.volumen,
  ];
  for (const value of candidates) {
    const text = String(value ?? '').trim();
    if (!text) continue;
    if (/\b(?:indices|index[_\s-]*cfd|cfd[_\s-]*index|cash[_\s-]*index)\b/i.test(text)) return true;
  }
  return false;
}

function isExplicitCashIndexHint(symbol: unknown, hint?: unknown, row?: Record<string, any> | null): boolean {
  const raw = String(symbol ?? '').trim().toUpperCase();
  if (raw.startsWith('^')) return true;
  const candidates = [
    hint,
    row?.instrumentType,
    row?.instrument_type,
    row?.instrument,
    row?.securityType,
    row?.secType,
  ];
  for (const value of candidates) {
    const text = String(value ?? '').trim().toUpperCase();
    if (text === 'CASH_INDEX' || text === 'IND' || /\bCASH\s+INDEX\b/.test(text)) return true;
  }
  return false;
}

export function resolveIndexInstrument(
  symbol: unknown,
  hint?: unknown,
  row?: Record<string, any> | null,
): ResolvedIndexInstrument | null {
  const rawSymbol = String(symbol ?? '').trim().toUpperCase();
  if (!rawSymbol) return null;

  const normalizedCandidate = normalizeIndexCfdCandidate(rawSymbol);
  if (!normalizedCandidate) return null;

  const hasHint = hasIndexCfdHint(hint, row);
  const explicitCash = isExplicitCashIndexHint(rawSymbol, hint, row);
  const directDef = ALIAS_TO_DEFINITION.get(normalizedCandidate);
  const hintDef = (hasHint || rawSymbol.startsWith('^')) ? HINT_ONLY_INDEX_ALIASES.get(normalizedCandidate) : null;
  const matched = directDef || hintDef;

  if (matched) {
    const instrumentType: IndexInstrumentType = explicitCash ? 'CASH_INDEX' : 'INDEX_CFD';
    return {
      symbol: rawSymbol,
      canonicalSymbol: matched.canonicalSymbol,
      indexName: matched.indexName,
      assetClass: 'STOCKS',
      instrumentType,
      isRecognizedInstrument: true,
      isSupportedAssetClass: true,
      chartSupported: false,
      chartSymbol: null,
    };
  }

  if (hasHint) {
    const instrumentType: IndexInstrumentType = explicitCash ? 'CASH_INDEX' : 'INDEX_CFD';
    return {
      symbol: rawSymbol,
      canonicalSymbol: normalizedCandidate,
      indexName: rawSymbol,
      assetClass: 'STOCKS',
      instrumentType,
      isRecognizedInstrument: true,
      isSupportedAssetClass: true,
      chartSupported: false,
      chartSymbol: null,
    };
  }

  return null;
}

export function isIndexOrCfdInstrument(
  symbol: unknown,
  hint?: unknown,
  row?: Record<string, any> | null,
): boolean {
  if (resolveIndexInstrument(symbol, hint, row)) return true;
  const typeText = String(
    hint ?? row?.instrumentType ?? row?.instrument_type ?? row?.metadata?.instrumentType ?? ''
  ).trim().toUpperCase();
  return typeText === 'INDEX_CFD' || typeText === 'CASH_INDEX';
}

export function formatInstrumentTypeLabel(instrumentType?: unknown, assetClass?: unknown): string {
  const normType = String(instrumentType ?? '').trim().toUpperCase();
  if (normType === 'INDEX_CFD') return 'Index CFD';
  if (normType === 'CASH_INDEX') return 'Cash Index';
  if (normType === 'FUTURES' || normType === 'FUT') return 'Futures';
  if (normType === 'FOREX' || normType === 'CASH' || normType === 'FX') return 'Forex';
  if (normType === 'CRYPTO') return 'Crypto';
  if (normType === 'ETF') return 'ETF';
  if (normType === 'STOCKS' || normType === 'STK' || normType === 'STOCK') return 'Stocks';

  const normAsset = String(assetClass ?? '').trim().toUpperCase();
  if (normAsset === 'FUTURES' || normAsset === 'FUT') return 'Futures';
  if (normAsset === 'FOREX' || normAsset === 'CASH' || normAsset === 'FX') return 'Forex';
  if (normAsset === 'CRYPTO') return 'Crypto';
  if (normAsset === 'STOCKS' || normAsset === 'STK') return 'Stocks';
  return 'Unsupported instrument';
}

export function formatUnsupportedInstrumentMessage(
  symbol?: unknown,
  instrumentType?: unknown,
  assetClass?: unknown,
): string {
  const cleanSymbol = String(symbol ?? '').trim().toUpperCase();
  const typeLabel = formatInstrumentTypeLabel(instrumentType, assetClass);
  if (cleanSymbol) {
    return `${cleanSymbol} (${typeLabel}) isn't supported by this workspace. Choose a supported instrument to continue.`;
  }
  return `This instrument (${typeLabel}) isn't supported by this workspace. Choose a supported instrument to continue.`;
}
